import type { ApolloClient } from "@apollo/client";
import type { IntlShape } from "react-intl";
import type { ExtractionJobResultQuery } from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { logApplicationFailure } from "@/lib/user-error";
import { levelFromPercent } from "@/lib/skill-levels";

export type ExtractionJobRecord = ExtractionJobResultQuery["extractionJob"];

export interface ProcessResult {
  // Read only when `documents` is empty: neither the event nor the job carried the payload, and this
  // is the sole surviving signal. Never rendered.
  status: string;
  documents: readonly { ref: string; status: string; fileName?: string | null }[];
}

export function resultOrStandIn(result: ProcessResult | null | undefined, completed: boolean): ProcessResult {
  return result ?? { status: completed ? "success" : "error", documents: [] };
}

/** null hides the block: the run did not look for skills, or genesis recorded no changes for it. */
export function addedSkills(
  job: ExtractionJobRecord,
  intl: IntlShape,
): { skill: string; level: string | null }[] | null {
  if (!job.extractSkills || !job.changes) return null;
  return job.changes.addedExpertises.map((e) => {
    const level = levelFromPercent(e.level);
    return {
      skill: e.description,
      level: level ? intl.formatMessage({ id: level.nameId, defaultMessage: level.name }) : null,
    };
  });
}

/** genesis lists every power the run wrote, also one re-scored to its old value; only a move counts. */
export function movedPowers(job: ExtractionJobRecord): { element: string; score: number }[] | null {
  if (!job.extractPowers || !job.changes) return null;
  return job.changes.scoredPowers
    .filter((p) => p.previousScore !== p.score)
    .map((p) => ({ element: p.element, score: p.score }));
}

/** @adr SPARK-0045-a-power-with-no-indication-is-counted-not-explained@1 */
export function unscoredPowerCount(scored: readonly { element: string }[]): number {
  const elements = new Set(scored.map((s) => s.element));
  return Object.keys(ELEMENT_CODES).filter((e) => !elements.has(e)).length;
}

/** The profile lists skills through both queries; only the mounted ones re-run, as in agent-refresh. */
export function refreshProfileSkills(apollo: ApolloClient): void {
  void apollo
    .refetchQueries({ include: ["Myself", "MyselfExpertiseData"] })
    .catch(() => logApplicationFailure("save.extraction.profile-refresh", { level: "warn" }));
}
